import type { DatePickerProps } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers';
import { isDate, isNotNullOrUndefined, vyDate, type VyDate } from '@vy/utils';
import React, { useMemo, useState } from 'react';
import { Size, SIZES } from '../../types/sizes';
import { Calendar } from '@vy/ui-design-system';
import { Box } from '@mui/material';
import { jsDateToPickerValue, pickerValueToJsDate } from './picker-value-utils';

export type VyDatePickerProps = Omit<DatePickerProps, 'onChange' | 'value'> & {
  value?: Date | null;
  onChange: (date: Date | null) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  size?: Extract<Size, typeof SIZES.SMALL | typeof SIZES.MEDIUM>;
  variant?: 'outlined' | 'filled' | 'standard';
  fullWidth?: boolean;
  required?: boolean;
  placeholder?: string;
  disableManualInput?: boolean;
  customBottomContent?: React.ReactNode;
};

type OpenPickerIconProps = {
  disabled?: boolean;
};

const OpenPickerIcon: React.FC<OpenPickerIconProps> = ({ disabled }) => (
  <Box
    sx={{
      display: 'inline-flex',
      '& svg path': {
        stroke: disabled ? '#C0C7D4' : undefined,
      },
    }}
  >
    <Calendar />
  </Box>
);

export const VyDatePicker: React.FC<VyDatePickerProps> = ({
  value,
  onChange,
  slotProps,
  disableManualInput = false,
  customBottomContent,
  ...datePickerProps
}) => {
  const isValidDate = !isDate(value);
  const [editing, setEditing] = useState(false);
  const isDisabled = datePickerProps.disabled ?? false;

  const handleDateChange = (newDate: VyDate | null) => {
    onChange(pickerValueToJsDate(newDate));
  };

  const handleBlur = () => {
    const isValidDate = vyDate(value).isValid();
    if (!isValidDate) {
      handleDateChange(null);
    }

    setEditing(false);
  };

  const format = useMemo(() => {
    const defaultFormat = 'MMMM DD, YY';

    if (disableManualInput) {
      return defaultFormat;
    }

    return editing || isValidDate ? 'MM/DD/YY' : defaultFormat;
  }, [disableManualInput, editing, isValidDate]);

  return (
    <DatePicker
      value={jsDateToPickerValue(value)}
      onChange={handleDateChange}
      format={format}
      onAccept={(value: VyDate | null, context) => {
        setEditing(false);
        datePickerProps?.onAccept?.(value, context);
      }}
      onOpen={() => {
        setEditing(true);
        datePickerProps.onOpen?.();
      }}
      onClose={() => {
        setEditing(false);
        datePickerProps?.onClose?.();
      }}
      slots={{
        openPickerIcon: OpenPickerIcon,
        layout: ({ children }) => (
          <>
            {children}
            {isNotNullOrUndefined(customBottomContent) && (
              <Box
                sx={{
                  padding: "0 30px 10px 30px",
                  marginTop: "-35px"
                }}>
                {customBottomContent}
              </Box>
            )}
          </>
        ),
      }}
      slotProps={{
        openPickerIcon: {
          disabled: isDisabled,
        } as object,
        textField: {
          slotProps: {
            htmlInput: {
              readOnly: disableManualInput,
              placeholder: datePickerProps.placeholder,
            },
            // v9's MuiPickersTextField renders an outlined-variant label that floats
            // into the notch. The app theme only styles MuiTextField's filled label
            // (static, above the field) — which v9 pickers no longer render — so
            // replicate that here to keep the v6 label placement.
            inputLabel: {
              sx: {
                position: 'static',
                transform: 'none',
                fontSize: '12px',
                marginBottom: '8px',
                // v9 turns the label blue on focus; v6 kept it a constant grey.
                color: 'rgba(27, 61, 85, 0.6)',
                '&.Mui-focused': {
                  color: 'rgba(27, 61, 85, 0.6)',
                },
              },
            },
          },
          fullWidth: true,
          error: datePickerProps.error,
          helperText: datePickerProps.helperText,
          onFocus: () => setEditing(true),
          onBlur: handleBlur,
          sx: {
            ...(disableManualInput && {
              '& .MuiFilledInput-root': {
                background: 'none',
                width: '200px',
                paddingRight: 0,
              },
            }),

            // MUI X v9 rewrote the picker field: the old filled <TextField> (which
            // inherited the app theme's `variant: 'filled'` default) became
            // MuiPickersTextField with its own *outlined* variant, so the theme no
            // longer applies. Restore the filled look — gray fill, no box border,
            // 36px height — so the picker matches sibling form fields.
            '& .MuiPickersInputBase-root': {
              backgroundColor: 'rgba(13, 27, 39, 0.04)',
              borderRadius: '12px',
              height: '36px',
              minHeight: '36px',
              paddingLeft: '12px',
              '& .MuiPickersInputBase-sectionsContainer': {
                paddingTop: 0,
                paddingBottom: 0,
              },
              '& .MuiPickersOutlinedInput-notchedOutline': {
                border: 'none',
              },
              // v9 redraws the notched outline as a 2px blue border on hover/focus
              // (a higher-specificity rule than the resting one above); suppress it
              // in those states too so the borderless filled look is preserved.
              '&:hover .MuiPickersOutlinedInput-notchedOutline, &.Mui-focused .MuiPickersOutlinedInput-notchedOutline':
                {
                  border: 'none',
                },
              '&.Mui-disabled': {
                '& svg path': {
                  stroke: '#C0C7D4',
                },
              },
            },
          },
        },
        ...slotProps,
      }}
      {...datePickerProps}
    />
  );
};
